import { Formik } from "formik";
import { Grid } from "@mui/material";
import React from "react";

import { BaseButton } from "@/src/component/base/BaseButton";
import { NoSSR } from "@/src/component/partial/NoSSR";
import { TrackingLogic } from "@/src/logic";
import { StrapiComponentType, StrapiCollectionType } from "@/src/type";
import { FormHelper } from "@/src/util";

import { FormItem } from "../FormItem";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./Form.module.scss";
import { useMemo } from "react";

interface RenderSubmitButtonProps {
  isLoading?: boolean;
  label?: string;
  isDark?: boolean;
  lastStep?: boolean;
  showNextButton?: boolean;
}

interface FormProps {
  showNextButton?: boolean;
  lastStep?: boolean;
  form?: Pick<StrapiComponentType.Section.DynamicForm, "nextButtonLabel" | "submitButtonLabel">;
  step: Pick<StrapiComponentType.Object.DynamicFormStep, "form_items">;
  crmLinkId: string;
  onSubmit: (formData: Record<string, any>, currentStep: number) => void;
  formId: string;
  isDark?: boolean;
  disableAnimatedLabel?: boolean;
  currentStep?: number;
  isLoading?: boolean;
  renderSubmitButton?: (props: RenderSubmitButtonProps) => React.ReactNode;
  renderHiddenInputs?: React.ReactNode;
}

function Form({
  step,
  crmLinkId,
  onSubmit,
  formId,
  isDark,
  disableAnimatedLabel,
  form,
  showNextButton,
  lastStep,
  currentStep,
  isLoading,
  renderSubmitButton,
  renderHiddenInputs,
}: FormProps) {
  const getInitialValue = (item: StrapiCollectionType.FormItem) => {
    switch (item.type) {
      case "Checkbox":
        return item.defaultValue === "true";
      case "DatePicker":
        return new Date();
      default:
        return "";
    }
  };

  const initialValues = useMemo(
    () =>
      (step.form_items.data ?? []).reduce(
        (acc, item) => {
          acc[item.attributes.definedProperty ?? item.attributes.jsonPropertyName] = getInitialValue(item.attributes);
          return acc;
        },
        {} as Record<string, any>
      ),
    [step.form_items.data]
  );

  const validationSchema = useMemo(() => FormHelper.validationRulesToYupSchema(step), [step]);

  function getMd(item: StrapiCollectionType.FormItem) {
    if (item.md) {
      return item.md;
    }
    return item.type === "TextArea" || step.form_items.data.length === 1 ? 12 : 6;
  }

  function handleOnSubmit(formData: Record<string, any>) {
    onSubmit(formData, currentStep ?? 0);
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={false}
      validateOnMount={false}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {(formik) => (
        <form
          onSubmit={formik.handleSubmit}
          id={formId ?? "ContactForm"}
          suppressHydrationWarning={true}
        >
          <div className={styles.form}>
            <Grid
              container
              spacing={2}
            >
              {(step.form_items.data ?? []).map((item) => (
                <Grid
                  item
                  key={item.id}
                  xs={item.attributes.xs ?? 12}
                  md={getMd(item.attributes)}
                  sx={{ width: "100%" }}
                >
                  <FormItem
                    //@ts-ignore - this error is not correct
                    item={{ ...item.attributes, id: item.id }}
                    formik={formik}
                    isDark={isDark}
                    disableAnimatedLabel={disableAnimatedLabel}
                  />
                </Grid>
              ))}
            </Grid>
            <NoSSR>
              <div
                className={styles.hidden}
                suppressHydrationWarning={true}
              >
                <input
                  type="text"
                  name="utmSource"
                  id="utmSource"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().utmSource ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="utmMedium"
                  id="utmMedium"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().utmMedium ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="utmTerm"
                  id="utmTerm"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().utmTerm ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="utmCampaign"
                  id="utmCampaign"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().utmCampaign ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="utmContent"
                  id="utmContent"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().utmContent ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="facebookCLID"
                  id="facebookCLID"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().facebookCLID ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="googleCLID"
                  id="googleCLID"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().googleCLID ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="referrer"
                  id="referrer"
                  readOnly={true}
                  value={TrackingLogic.getUTMParams().referrer ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="marketingSource"
                  id="marketingSource"
                  readOnly={true}
                  value={TrackingLogic.getMarketingSource() ?? ""}
                  onChange={() => {}}
                />
                <input
                  type="text"
                  name="crmLinkId"
                  id="crmLinkId"
                  readOnly={true}
                  value={crmLinkId ?? ""}
                  onChange={() => {}}
                />
                {renderHiddenInputs && <>{renderHiddenInputs}</>}
              </div>
            </NoSSR>
            <div className={styles.formBtn}>
              {renderSubmitButton ? (
                renderSubmitButton({
                  isLoading,
                  label: (showNextButton ? form?.nextButtonLabel : form?.submitButtonLabel) ?? "Submit button",
                  isDark,
                  lastStep,
                  showNextButton,
                })
              ) : (
                <BaseButton
                  id="contactForm.submitButton"
                  aria-label={(showNextButton ? form?.nextButtonLabel : form?.submitButtonLabel) ?? "Submit button"}
                  type="submit"
                  variant={lastStep ? "contained" : "outlined"}
                  loading={isLoading}
                  color={isDark || lastStep ? "primary" : "secondary"}
                  size="large"
                  sx={{
                    "&:disabled": {
                      borderColor: `${isDark || lastStep ? theme.primaryMain : theme.secondaryMain} !important`,
                      color: `${isDark || lastStep ? theme.primaryMain : theme.secondaryMain} !important`,
                    },
                  }}
                >
                  {showNextButton ? form?.nextButtonLabel : form?.submitButtonLabel}
                </BaseButton>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}

export default Form;
